import { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface FavoriteButtonProps {
	entityType: 'candidate' | 'job' | 'company' | 'list' | 'query';
	entityId: string;
	entityName?: string;
	variant?: 'default' | 'outline' | 'ghost';
	size?: 'default' | 'sm' | 'lg' | 'icon';
}

export const FavoriteButton = ({
	entityType,
	entityId,
	entityName,
	variant = 'outline',
	size = 'icon'
}: FavoriteButtonProps) => {
	const { t } = useTranslation();
	const { user, currentOrganization } = useAuth();
	const [isFavorite, setIsFavorite] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (user && entityId)
			checkFavoriteStatus();
	}, [user, entityId, entityType]);

	const checkFavoriteStatus = async () => {
		if (!user || !entityId)
			return;

		try {
			const { data, error } = await supabase
				.from('user_favorite')
				.select('id')
				.eq('user_id', user.id)
				.eq('entity_type', entityType)
				.eq('entity_id', entityId)
				.maybeSingle();

			if (error)
				throw error;

			setIsFavorite(!!data);
		} catch (error) {
			console.error('Error checking favorite status:', error);
		}
	};

	const toggleFavorite = async () => {
		if (!user || !entityId) {
			toast({
				title: t('common.error', 'Error'),
				description: t('favorites.loginRequired', 'You must be logged in to favorite items'),
				variant: 'destructive'
			});
			return;
		}

		setIsLoading(true);

		try {
			if (isFavorite) {
				// Remove from favorites
				const { error } = await supabase
					.from('user_favorite')
					.delete()
					.eq('user_id', user.id)
					.eq('entity_type', entityType)
					.eq('entity_id', entityId);

				if (error)
					throw error;

				setIsFavorite(false);
				toast({
					title: t('favorites.removed', 'Removed from favorites'),
					description: entityName ? t('favorites.removedDescription', { name: entityName }) : undefined
				});
			} else {
				// Add to favorites
				if (!currentOrganization?.id) {
					throw new Error('No organization selected');
				}

				const { error } = await supabase
					.from('user_favorite')
					.insert({
						user_id: user.id,
						entity_type: entityType,
						entity_id: entityId,
						organization_uuid: currentOrganization.id
					});

				if (error)
					throw error;

				setIsFavorite(true);
				toast({
					title: t('favorites.added', 'Added to favorites'),
					description: entityName ? t('favorites.addedDescription', { name: entityName }) : undefined
				});
			}
		} catch (error) {
			console.error('Error toggling favorite:', error);
			toast({
				title: t('common.error', 'Error'),
				description: t('favorites.toggleError', 'Failed to update favorite status'),
				variant: 'destructive'
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Button
			variant={variant}
			size={size}
			className="flex items-center justify-center"
			onClick={toggleFavorite}
			disabled={isLoading}
			title={isFavorite ? t('favorites.removeFromFavorites', 'Remove from favorites') : t('favorites.addToFavorites', 'Add to favorites')}
		>
			<Star
				className={`h-4 w-4 ${isFavorite ? 'fill-yellow-400 text-yellow-400' : ''}`}
			/>
		</Button>
	);
};
